/**
 * @file index.tsx
 *
 * @fileoverview Renders autocomplete search facet popover.
 */
import { ReactNode, SyntheticEvent, useState } from "react";
import ArrowDownIcon from "@jq-icons/ArrowDown";
import ArrowUpIcon from "@jq-icons/ArrowUp";
import CloseIcon from "@jq-icons/Close";
import Input from "../../../../02-atoms/01-forms/03-input";
import { Popover } from "../../../../02-atoms/16-popover";
import styles from "../facet.module.scss";
import HiddenVisually from "../../../../02-atoms/14-w-a11y/02-visually-hidden";

interface AutocompleteFacetPopoverProps {
  /**
   * The content to go into the facet popover.
   */
  children: ReactNode;
  /**
   * The input placeholder.
   */
  placeholder?: string;
  /**
   * The search value.
   */
  searchValue: string;
  /**
   * The onSearch handler.
   */
  onSearch: (searchValue: string) => void;
  /**
   * The onClear handler.
   */
  onClear?: () => void;
  /**
   * The close label.
   */
  closeLabel?: string;
  /**
   * The toggle label.
   */
  toggleLabel?: string;
}

export default function AutocompleteFacetPopover({
  children,
  searchValue,
  placeholder,
  onSearch,
  onClear,
  closeLabel = "Close",
  toggleLabel = "Toggle",
}: AutocompleteFacetPopoverProps) {
  const [popoverOpened, setPopoverOpened] = useState(false);

  return (
    <div className={styles.wrapper}>
      <Popover
        onOpenAutoFocus={(event: Event) => {
          // Don't focus on popover when we click on input because
          // input will not be accessible.
          event.preventDefault();
        }}
        onOutsideClick={() => setPopoverOpened(false)}
        maxHeight="250px"
        onToggle={setPopoverOpened}
        triggerAsChild
        trigger={
          // This is a bit hacky. But only buttons and anchors and lists can have ARIA attributes like
          // aria-expanded. So we have to wrap the input in a list. This will avoid the warning that a div cannot have
          // aria-expanded. Making the input inside of a button will also trigger a warning that interactive elements can't be nested. So this
          // is the best solution for this component. Or we change it more so that e.g. it does not use an input.
          <ul>
            <li>
              <Input
                className={styles.facetInput}
                placeholder={placeholder}
                value={searchValue}
                onChange={(event: SyntheticEvent<HTMLInputElement>) => {
                  onSearch(event.currentTarget.value);
                }}
              />
            </li>
          </ul>
        }
        opened={popoverOpened}
      >
        <div className={styles.facet}>{children}</div>{" "}
      </Popover>
      <div className={styles.iconWrapper}>
        {searchValue && onClear ? (
          <button
            onClick={(event) => {
              onClear();
              setPopoverOpened(false);
              event.preventDefault();
            }}
          >
            <HiddenVisually>
              <span>{closeLabel}</span>
            </HiddenVisually>
            <CloseIcon />
          </button>
        ) : (
          <button onClick={() => setPopoverOpened(!popoverOpened)}>
            <HiddenVisually>
              <span>{toggleLabel}</span>
            </HiddenVisually>
            {popoverOpened ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

export { AutocompleteFacetPopover };
